import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { keyframes } from "@emotion/react";
import {
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SelectedGame } from "../../types/game";

export interface GameWheelRenderOptions {
  peek: boolean;
  expanded: boolean;
  onExpandedChange?: (open: boolean) => void;
}

interface GameWheelProps {
  games: SelectedGame[];
  activeGameId?: number;
  onActiveGameChange: (gameId: number) => void;
  onExitWheel: () => void;
  renderGame: (
    game: SelectedGame,
    options: GameWheelRenderOptions,
  ) => React.ReactNode;
}

const enterFromBelow = keyframes`
  from { opacity: .3; transform: translateY(34px) scale(.94); }
  to { opacity: 1; transform: translateY(0); }
`;

const enterFromAbove = keyframes`
  from { opacity: .3; transform: translateY(-34px) scale(.94); }
  to { opacity: 1; transform: translateY(0); }
`;

const exitToAbove = keyframes`
  from { opacity: 1; transform: translateY(0); }
  to { opacity: .3; transform: translateY(-34px) scale(.94); }
`;

const exitToBelow = keyframes`
  from { opacity: 1; transform: translateY(0); }
  to { opacity: .3; transform: translateY(34px) scale(.94); }
`;

const isInteractive = (target: EventTarget | null) =>
  target instanceof Element &&
  Boolean(
    target.closest(
      "button, a, input, select, textarea, [role='button'], [contenteditable='true']",
    ),
  );

const gameLabel = (game?: SelectedGame) =>
  game ? `${game.awayTeam.school} at ${game.homeTeam.school}` : "game";
const setInert = (node: HTMLDivElement | null) => {
  if (node) node.setAttribute("inert", "");
};


interface WheelTransition {
  from: SelectedGame;
  to: SelectedGame;
  direction: 1 | -1;
}

const GameWheel = ({
  games,
  activeGameId,
  onActiveGameChange,
  onExitWheel,
  renderGame,
}: GameWheelProps) => {
  const stageRef = useRef<HTMLDivElement>(null);
  const wheelDelta = useRef(0);
  const wheelResetTimer = useRef<ReturnType<typeof setTimeout>>();
  const lastWheelDirection = useRef<1 | -1>();
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const [transition, setTransition] = useState<WheelTransition | null>(null);
  const [expandedGameId, setExpandedGameId] = useState<number | null>(null);

  const activeIndex = useMemo(() => {
    const found = games.findIndex(
      (game) => Number(game.game.id) === Number(activeGameId),
    );
    return found >= 0 ? found : 0;
  }, [activeGameId, games]);
  const activeGame = games[activeIndex];
  const canLoop = games.length > 1;
  const previousGame = canLoop
    ? games[(activeIndex - 1 + games.length) % games.length]
    : undefined;
  const nextGame = canLoop
    ? games[(activeIndex + 1) % games.length]
    : undefined;
  const detailsExpanded =
    expandedGameId !== null &&
    Number(expandedGameId) === Number(activeGame?.game.id);

  useEffect(() => {
    if (activeGameId === undefined || !activeGame) return;
    const activeStillExists = games.some(
      (game) => Number(game.game.id) === Number(activeGameId),
    );
    if (!activeStillExists) {
      onActiveGameChange(Number(activeGame.game.id));
    }
  }, [activeGame, activeGameId, games, onActiveGameChange]);

  useEffect(() => {
    if (
      expandedGameId !== null &&
      !games.some((game) => Number(game.game.id) === expandedGameId)
    ) {
      setExpandedGameId(null);
    }
  }, [expandedGameId, games]);

  const move = useCallback((nextDirection: 1 | -1) => {
    if (!canLoop || !activeGame || transition) return;
    setExpandedGameId(null);
    const nextIndex =
      (activeIndex + nextDirection + games.length) % games.length;
    const nextGame = games[nextIndex];
    if (reduceMotion) {
      onActiveGameChange(Number(nextGame.game.id));
    } else {
      setTransition({
        from: activeGame,
        to: nextGame,
        direction: nextDirection,
      });
    }
  }, [
    activeGame,
    activeIndex,
    canLoop,
    games,
    onActiveGameChange,
    reduceMotion,
    transition,
  ]);

  useEffect(() => {
    if (!transition) return undefined;
    const timer = window.setTimeout(() => {
      onActiveGameChange(Number(transition.to.game.id));
      setTransition(null);
    }, 220);
    return () => window.clearTimeout(timer);
  }, [onActiveGameChange, transition]);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return undefined;

    const handleWheel = (event: WheelEvent) => {
      if (
        !canLoop ||
        detailsExpanded ||
        isInteractive(event.target) ||
        Math.abs(event.deltaY) <= Math.abs(event.deltaX)
      ) {
        return;
      }

      event.preventDefault();
      if (transition) return;

      const nextDirection: 1 | -1 = event.deltaY > 0 ? 1 : -1;
      if (
        lastWheelDirection.current !== undefined &&
        lastWheelDirection.current !== nextDirection
      ) {
        wheelDelta.current = 0;
      }
      lastWheelDirection.current = nextDirection;
      wheelDelta.current += event.deltaY;

      if (wheelResetTimer.current) clearTimeout(wheelResetTimer.current);
      wheelResetTimer.current = setTimeout(() => {
        wheelDelta.current = 0;
        lastWheelDirection.current = undefined;
      }, 140);

      if (Math.abs(wheelDelta.current) < 60) return;
      wheelDelta.current = 0;
      lastWheelDirection.current = undefined;
      move(nextDirection);
    };

    stage.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      stage.removeEventListener("wheel", handleWheel);
      if (wheelResetTimer.current) clearTimeout(wheelResetTimer.current);
    };
  }, [canLoop, detailsExpanded, move, transition]);

  if (!activeGame) return null;

  const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    if (detailsExpanded || isInteractive(event.target)) {
      touchStart.current = null;
      return;
    }
    const touch = event.touches[0];
    touchStart.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    const start = touchStart.current;
    touchStart.current = null;
    if (!start || !canLoop || detailsExpanded || transition) return;
    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - start.x;
    const deltaY = touch.clientY - start.y;
    if (Math.abs(deltaY) < 44 || Math.abs(deltaY) < Math.abs(deltaX) * 1.25) {
      return;
    }
    move(deltaY < 0 ? 1 : -1);
  };

  return (
    <Box component="section" aria-labelledby="game-wheel-heading">
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-end"
        gap={2}
        mb={2}
      >
        <Box>
          <Typography id="game-wheel-heading" variant="h5" fontWeight={800}>
            Choose your game
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Spin through the week one matchup at a time
          </Typography>
        </Box>
        <Stack
          direction="row"
          alignItems="center"
          gap={1}
          sx={{ display: { xs: "none", md: "flex" } }}
        >
          <Box
            component="img"
            src="/assets/icons/infinite-loop.svg"
            alt=""
            aria-hidden
            sx={{ width: 20, height: 20 }}
          />
          <Typography variant="overline" color="text.secondary">
            Infinite wheel · scroll or use ↑ ↓
          </Typography>
        </Stack>
      </Stack>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          minHeight: 38,
          mb: 2,
          px: 1.5,
          border: 1,
          borderColor: "divider",
          borderRadius: 99,
          bgcolor: "surface.high",
        }}
      >
        <Typography variant="overline">
          Game {activeIndex + 1} of {games.length}
          {canLoop ? " · Loops" : ""}
        </Typography>
        <Typography variant="overline" color="text.secondary">
          <Box component="span" sx={{ display: { xs: "inline", md: "none" } }}>
            Swipe ↑ ↓
          </Box>
          <Box component="span" sx={{ display: { xs: "none", md: "inline" } }}>
            Scroll ↑ ↓
          </Box>
        </Typography>
      </Stack>

      <Box
        ref={stageRef}
        data-testid="game-wheel"
        tabIndex={0}
        aria-label={`Game Wheel. ${gameLabel(activeGame)}, game ${activeIndex + 1} of ${games.length}. Use up and down arrows to change games.`}
        onKeyDown={(event) => {
          if (detailsExpanded || transition) return;
          if (event.key === "ArrowUp") {
            event.preventDefault();
            move(-1);
          }
          if (event.key === "ArrowDown") {
            event.preventDefault();
            move(1);
          }
        }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        sx={{
          position: "relative",
          height: { xs: 700, md: 834 },
          maxWidth: 900,
          mx: "auto",
          overflow: "hidden",
          border: 1,
          borderColor: "divider",
          borderRadius: { xs: 3, md: 5 },
          bgcolor: "surface.container",
          touchAction: detailsExpanded ? "pan-y" : "none",
          outline: "none",
          "&:focus-visible": {
            boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}`,
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: "50%",
            width: { xs: "100%", md: 600 },
            maxWidth: "100%",
            transform: "translateX(-50%)",
            overflow: "hidden",
            bgcolor: "surface.high",
          }}
        >
          {!detailsExpanded && previousGame && (
            <Box
              ref={setInert}
              aria-hidden="true"
              data-testid="game-wheel-previous-peek"
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: { xs: 74, md: 116 },
                overflow: "hidden",
                display: "grid",
                justifyContent: "center",
                opacity: 0.3,
                pointerEvents: "none",
                "& > *": {
                  transform: { xs: "scale(.878)", md: "scale(.897)" },
                  transformOrigin: "top center",
                },
              }}
            >
              {renderGame(previousGame, {
                peek: true,
                expanded: false,
              })}
            </Box>
          )}

          {!detailsExpanded && nextGame && (
            <Box
              ref={setInert}
              aria-hidden="true"
              data-testid="game-wheel-next-peek"
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: { xs: 74, md: 116 },
                overflow: "hidden",
                display: "grid",
                justifyContent: "center",
                opacity: 0.3,
                pointerEvents: "none",
                "& > *": {
                  transform: { xs: "scale(.878)", md: "scale(.897)" },
                  transformOrigin: "top center",
                },
              }}
            >
              {renderGame(nextGame, { peek: true, expanded: false })}
            </Box>
          )}

          <Box
            aria-hidden
            sx={{
              position: "absolute",
              zIndex: 1,
              top: { xs: 54, md: 117 },
            visibility: detailsExpanded ? "hidden" : "visible",
              left: "50%",
              width: { xs: 328, md: 336 },
              height: { xs: 592, md: 600 },
              maxWidth: "calc(100% - 20px)",
              transform: "translateX(-50%)",
              border: 2,
              borderColor: "primary.main",
              borderRadius: "22px",
              boxShadow: "0 14px 28px rgba(0,0,0,.28), 0 0 24px rgba(41,209,171,.28)",
            }}
          />

          <Box
            data-testid="game-wheel-active"
            sx={{
              position: "absolute",
              zIndex: 2,
              top: detailsExpanded ? 12 : { xs: 62, md: 129 },
              left: "50%",
              width: 312,
              height: 576,
              transform: "translateX(-50%)",
              transition: reduceMotion ? "none" : "top 180ms ease-out",
            }}
          >
            {transition ? (
              <>
                <Box
                  ref={setInert}
                  aria-hidden="true"
                  sx={{
                    position: "absolute",
                    inset: 0,
                    pointerEvents: "none",
                    animation: `${
                      transition.direction > 0 ? exitToAbove : exitToBelow
                    } 220ms ease-in-out forwards`,
                  }}
                >
                  {renderGame(transition.from, {
                    peek: true,
                    expanded: false,
                  })}
                </Box>
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    pointerEvents: "none",
                    animation: `${
                      transition.direction > 0 ? enterFromBelow : enterFromAbove
                    } 220ms ease-in-out forwards`,
                  }}
                >
                  {renderGame(transition.to, {
                    peek: false,
                    expanded: false,
                  })}
                </Box>
              </>
            ) : (
              renderGame(activeGame, {
                peek: false,
                expanded: detailsExpanded,
                onExpandedChange: (open) =>
                  setExpandedGameId(open ? Number(activeGame.game.id) : null),
              })
            )}
          </Box>
        </Box>

        <IconButton
          aria-label={`Previous game: ${gameLabel(previousGame)}`}
          onClick={() => move(-1)}
          disabled={!canLoop || Boolean(transition)}
          sx={{
            position: "absolute",
            zIndex: 4,
            minWidth: 44,
            visibility: detailsExpanded ? "hidden" : "visible",
            width: { xs: 44, md: 52 },
            height: { xs: 44, md: 52 },
            top: { xs: 10, md: "calc(50% - 58px)" },
            left: { xs: "50%", md: "auto" },
            right: { xs: "auto", md: 64 },
            transform: { xs: "translateX(-50%)", md: "none" },
            border: 1,
            borderColor: "divider",
            bgcolor: "surface.high",
            boxShadow: 4,
            "&:hover": { bgcolor: "surface.high" },
          }}
        >
          <KeyboardArrowUpRoundedIcon />
        </IconButton>

        <IconButton
          aria-label={`Next game: ${gameLabel(nextGame)}`}
          onClick={() => move(1)}
          disabled={!canLoop || Boolean(transition)}
          sx={{
            position: "absolute",
            zIndex: 4,
            minWidth: 44,
            visibility: detailsExpanded ? "hidden" : "visible",
            width: { xs: 44, md: 52 },
            height: { xs: 44, md: 52 },
            bottom: { xs: 10, md: "calc(50% - 58px)" },
            right: { xs: "auto", md: 64 },
            left: { xs: "50%", md: "auto" },
            transform: { xs: "translateX(-50%)", md: "none" },
            border: 1,
            borderColor: "divider",
            bgcolor: "surface.high",
            boxShadow: 4,
            "&:hover": { bgcolor: "surface.high" },
          }}
        >
          <KeyboardArrowDownRoundedIcon />
        </IconButton>

        <Button
          variant="contained"
          color="inherit"
          startIcon={<VisibilityOutlinedIcon />}
          onClick={onExitWheel}
          sx={{
            display: { xs: "flex", md: "none" },
            position: "absolute",
            zIndex: 4,
            top: 10,
            visibility: detailsExpanded ? "hidden" : "visible",
            right: 10,
            minHeight: 44,
            borderRadius: 99,
            bgcolor: "surface.high",
            color: "text.primary",
            boxShadow: 4,
          }}
        >
          Exit wheel
        </Button>

        <Box
          aria-live="polite"
          sx={{
            position: "absolute",
            width: 1,
            height: 1,
            p: 0,
            m: -1,
            overflow: "hidden",
            clip: "rect(0 0 0 0)",
            whiteSpace: "nowrap",
            border: 0,
          }}
        >
          {gameLabel(activeGame)}, game {activeIndex + 1} of {games.length}
        </Box>
      </Box>
    </Box>
  );
};

export default GameWheel;
